// src/pages/SheetView.js
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Stack,
  Typography,
  Chip,
  Divider,
  Skeleton,
  ToggleButton,
  ToggleButtonGroup,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
  Button,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import { useNavigate, useParams } from "react-router-dom";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { collection, doc, getDoc, getDocs, limit, query, setDoc } from "firebase/firestore";
import { db } from "../firebase-config";
import { MathJax, MathJaxContext } from "better-react-mathjax";

async function getRole(sheetId, uid) {
  if (!sheetId || !uid) return null;
  const m = await getDoc(doc(db, "sheets", sheetId, "members", uid));
  return m.exists() ? (m.data()?.role || null) : null;
}

/** cookie helpers */
function getCookie(name) {
  const v = `; ${document.cookie}`;
  const parts = v.split(`; ${name}=`);
  if (parts.length !== 2) return "";
  return parts.pop().split(";").shift() || "";
}
function setCookie(name, value, days = 365) {
  const d = new Date();
  d.setTime(d.getTime() + days * 24 * 60 * 60 * 1000);
  document.cookie = `${name}=${value}; expires=${d.toUTCString()}; path=/; SameSite=Lax`;
}

const PREF_COOKIE = "sheetViewPrefs_v3";

function isNumericLike(v) {
  if (v === null || v === undefined) return false;
  const s = String(v).trim();
  if (!s) return false;
  return !Number.isNaN(Number(s));
}

function varTooltipText(v) {
  const name = (v?.name || "").trim();
  const code = (v?.code || "").trim();
  const unit = (v?.unit || "").trim();
  const desc = (v?.description || "").trim();

  const parts = [];
  if (name) parts.push(name);
  if (unit) parts.push(`[${unit}]`);
  if (desc && desc.toLowerCase() !== name.toLowerCase()) parts.push(desc);
  if (!parts.length && code) parts.push(code);
  return parts.join(" • ");
}

// MathJax ONLY for equations or numeric display (as requested)
function MathInline({ tex }) {
  if (!tex) return null;
  return <MathJax>{"\\(" + tex + "\\)"}</MathJax>;
}

function sanitiseCodeLabel(raw) {
  // You said: don't show things like "\rho" in a chip.
  // Since MathJax is NOT allowed for non-numeric, strip leading "\" for display only.
  const s = String(raw || "").trim();
  if (!s) return "";
  return s.startsWith("\\") ? s.slice(1) : s;
}

function VarChip({ v }) {
  const raw = (v?.code || "").trim() || (v?.name || "").trim() || "(var)";
  const tip = varTooltipText(v);

  // numeric => show f(number) with MathJax
  if (v?.type == "number"){ //isNumericLike(raw)) {
    const node = (
      <Box
        sx={{
          display: "inline-flex",
          alignItems: "center",
          px: 1,
          py: 0.2,
          mr: 1,
          mb: 1,
          fontSize: 13,
          lineHeight: 1,
          bgcolor: "background.paper",
        }}
      >
        <MathInline tex={v?.code} />
      </Box>
    );
    return tip ? (
      <Tooltip title={tip} arrow placement="top">
        <Box component="span">{node}</Box>
      </Tooltip>
    ) : (
      node
    );
  }

  // non-numeric => NO MathJax, show code text only (sanitised)
  const label = sanitiseCodeLabel(raw) || "(var)";
  const chip = <Chip size="small" variant="outlined" label={label} sx={{ mr: 1, mb: 1 }} />;

  return tip ? (
    <Tooltip title={tip} arrow placement="top">
      <Box component="span">{chip}</Box>
    </Tooltip>
  ) : (
    chip
  );
}

// Full vars: keep very small, one line, not the focus
function VarInline({ v }) {
  const code = (v?.code || "").trim() || (v?.name || "").trim() || "(var)";
  const unit = (v?.unit || "").trim();
  const name = (v?.name || "").trim();
  const desc = (v?.description || "").trim();

  const showDesc = desc && desc.toLowerCase() !== name.toLowerCase();
  const showName = name && name.toLowerCase() !== code.toLowerCase();

  const codePart = v?.type == "number" ? <MathInline tex={code} /> : sanitiseCodeLabel(code);

  return (
    <Typography variant="caption" sx={{ opacity: 0.85, lineHeight: 1.2 }}>
      <Box component="span" sx={{ fontWeight: 700 }}>
        {codePart}
      </Box>
      {unit ? <Box component="span" sx={{ opacity: 0.75 }}>{` [${unit}]`}</Box> : null}
      {showName ? <Box component="span" sx={{ opacity: 0.8 }}>{` — ${name}`}</Box> : null}
      {showDesc ? <Box component="span" sx={{ opacity: 0.65 }}>{` — ${desc}`}</Box> : null}
    </Typography>
  );
}

export default function SheetView() {
  const { sheetId } = useParams();
  const navigate = useNavigate();

  const [user, setUser] = useState(null);
  const [role, setRole] = useState(null);

  const [sheet, setSheet] = useState(null);
  const [vars, setVars] = useState([]);
  const [sections, setSections] = useState([]);
  const [items, setItems] = useState([]);

  const [loading, setLoading] = useState(true);

  const [viewType, setViewType] = useState("basic"); // simple | description | basic | full
  const [columns, setColumns] = useState(1);

  const prefsLoadedRef = useRef(false);
  const persistTimerRef = useRef(null);

  const mathjaxConfig = useMemo(
    () => ({
      loader: { load: ["input/tex", "output/chtml"] },
      tex: { inlineMath: [["$", "$"], ["\\(", "\\)"]] },
    }),
    []
  );

  useEffect(() => {
    const auth = getAuth();
    return onAuthStateChanged(auth, (u) => setUser(u || null));
  }, []);

  // load prefs
  useEffect(() => {
    let cancelled = false;

    async function loadPrefs() {
      prefsLoadedRef.current = false;
      try {
        if (user?.uid) {
          const prefRef = doc(db, "users", user.uid, "prefs", "sheetView");
          const prefSnap = await getDoc(prefRef);
          if (cancelled) return;

          if (prefSnap.exists()) {
            const p = prefSnap.data() || {};
            if (typeof p.viewType === "string") setViewType(p.viewType);
            if (Number.isFinite(p.columns)) setColumns(Math.max(1, Math.min(4, p.columns)));
          } else {
            const raw = getCookie(PREF_COOKIE);
            if (raw) {
              try {
                const p = JSON.parse(decodeURIComponent(raw));
                if (typeof p.viewType === "string") setViewType(p.viewType);
                if (Number.isFinite(p.columns)) setColumns(Math.max(1, Math.min(4, p.columns)));
              } catch {}
            }
          }
        } else {
          const raw = getCookie(PREF_COOKIE);
          if (raw) {
            try {
              const p = JSON.parse(decodeURIComponent(raw));
              if (typeof p.viewType === "string") setViewType(p.viewType);
              if (Number.isFinite(p.columns)) setColumns(Math.max(1, Math.min(4, p.columns)));
            } catch {}
          }
        }
      } finally {
        if (!cancelled) prefsLoadedRef.current = true;
      }
    }

    loadPrefs();
    return () => {
      cancelled = true;
    };
  }, [user?.uid]);

  // persist prefs
  useEffect(() => {
    if (!prefsLoadedRef.current) return;

    if (persistTimerRef.current) clearTimeout(persistTimerRef.current);
    persistTimerRef.current = setTimeout(async () => {
      const prefs = { viewType, columns };

      try {
        setCookie(PREF_COOKIE, encodeURIComponent(JSON.stringify(prefs)));
      } catch {}

      if (user?.uid) {
        try {
          const prefRef = doc(db, "users", user.uid, "prefs", "sheetView");
          await setDoc(prefRef, prefs, { merge: true });
        } catch {}
      }
    }, 250);

    return () => {
      if (persistTimerRef.current) clearTimeout(persistTimerRef.current);
    };
  }, [viewType, columns, user?.uid]);

  useEffect(() => {
    let cancelled = false;

    async function run() {
      setLoading(true);
      try {
        const s = await getDoc(doc(db, "sheets", sheetId));
        if (!s.exists()) {
          if (!cancelled) setSheet(null);
          return;
        }

        const sheetData = { id: s.id, ...s.data() };
        if (!cancelled) setSheet(sheetData);

        if (user?.uid) {
          const r = await getRole(sheetId, user.uid);
          if (!cancelled) setRole(r);
        } else {
          if (!cancelled) setRole(null);
        }

        const [vSnap, secSnap, itemSnap] = await Promise.all([
          getDocs(query(collection(db, "sheets", sheetId, "variables"), limit(500))).catch(() => null),
          getDocs(query(collection(db, "sheets", sheetId, "sections"), limit(500))).catch(() => null),
          getDocs(query(collection(db, "sheets", sheetId, "items"), limit(1000))).catch(() => null),
        ]);

        if (!cancelled) {
          const v = vSnap?.docs ? vSnap.docs.map((d) => ({ id: d.id, ...d.data() })) : null;
          const sec = secSnap?.docs ? secSnap.docs.map((d) => ({ id: d.id, ...d.data() })) : null;
          const it = itemSnap?.docs ? itemSnap.docs.map((d) => ({ id: d.id, ...d.data() })) : null;

          setVars(
            v ??
              (Array.isArray(sheetData.variables)
                ? sheetData.variables.map((x, i) => ({ id: x.id || `v${i}`, ...x }))
                : [])
          );

          setSections(
            sec ??
              (Array.isArray(sheetData.sections)
                ? sheetData.sections.map((x, i) => ({ id: x.id || `s${i}`, ...x }))
                : [])
          );

          if (it) {
            setItems(it);
          } else if (Array.isArray(sheetData.sections)) {
            const flat = [];
            sheetData.sections.forEach((sct, sIdx) => {
              (sct.items || []).forEach((x, i) => flat.push({ id: x.id || `s${sIdx}_i${i}`, sectionIdx: sIdx, ...x }));
            });
            setItems(flat);
          } else {
            setItems([]);
          }
        }
      } catch (e) {
        console.error(e);
        if (!cancelled) setSheet(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    run();
    return () => {
      cancelled = true;
    };
  }, [sheetId, user?.uid]);

  const isOwner = role === "owner";

  const showDescriptions = viewType === "description" || viewType === "full";
  const showVars = viewType === "basic" || viewType === "full";

  // Equation + function code are the most important thing and MUST show in ALL view types.
  // Only regular text description is hidden in "simple".
  const renderItemBody = (it) => {
    const type = (it.type || "text").toLowerCase();

    if (type === "equation") {
      const eq = (it.equation || "").trim();
      if (!eq) return null;
      return (
        <Typography variant="body2" sx={{ opacity: 0.95 }}>
          <MathInline tex={eq} />
        </Typography>
      );
    }

    if (type === "function") {
      const code = (it.code || "").trim();
      if (!code) return null;
      return (
        <Box
          component="pre"
          sx={{
            m: 0,
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
            fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
            fontSize: 13,
            opacity: 0.95,
          }}
        >
          {code}
        </Box>
      );
    }

    // other types: only show description outside simple
    if (viewType !== "simple" && (it.description || "").trim()) {
      return (
        <Typography variant="body2" sx={{ opacity: 0.9 }}>
          {it.description}
        </Typography>
      );
    }

    return null;
  };

  const normalisedSections = useMemo(() => {
    const secArr = Array.isArray(sections) ? sections : [];
    const itArr = Array.isArray(items) ? items : [];

    const list = secArr.map((s, sIdx) => {
      const sectionItems =
        (Array.isArray(s.items) && s.items) ||
        itArr.filter((it) => it.sectionId === s.id || it.sectionIdx === sIdx);

      const sectionVars = Array.isArray(s.variables) ? s.variables : [];

      // Remove empty sections: keep only if something will render for this view.
      const visibleItems = sectionItems.filter((it) => {
        const t = (it?.type || "text").toLowerCase();
        const hasName = !!(it?.name || "").trim();
        const hasDesc = !!(it?.description || "").trim();
        const hasEq = t === "equation" && !!(it?.equation || "").trim();
        const hasFn = t === "function" && !!(it?.code || "").trim();

        // Equations/functions must still appear even if no name.
        if (hasEq || hasFn) return true;

        if (viewType === "simple") return hasName; // simple: text items need a name
        if (viewType === "description") return hasName || hasDesc;
        return true; // basic/full
      });

      const hasTitle = !!(s.name || "").trim();
      const hasDesc = !!(s.description || "").trim();
      const hasVarsLocal = sectionVars.length > 0;
      const hasItems = visibleItems.length > 0;

      const keep =
        hasTitle &&
        (hasItems || (showDescriptions && hasDesc) || (showVars && hasVarsLocal));

      return {
        ...s,
        _idx: sIdx,
        _items: visibleItems,
        _vars: sectionVars,
        _keep: keep,
      };
    });

    return list.filter((s) => s._keep);
  }, [sections, items, viewType, showDescriptions, showVars]);

  const visibleGlobalVars = useMemo(() => {
    const arr = Array.isArray(vars) ? vars : [];
    if (!showVars) return [];
    return arr;
  }, [vars, showVars]);

  if (loading) {
    return (
      <Box sx={{ maxWidth: 1100, margin: "0 auto" }}>
        <Box sx={{ p: 2 }}>
          <Stack spacing={1}>
            <Skeleton height={40} />
            <Skeleton height={24} />
            <Skeleton height={240} />
          </Stack>
        </Box>
      </Box>
    );
  }

  if (!sheet) {
    return (
      <Box sx={{ maxWidth: 900, margin: "0 auto" }}>
        <Box sx={{ p: 2 }}>
          <Typography variant="h6">Sheet not found</Typography>
        </Box>
      </Box>
    );
  }

  return (
    <MathJaxContext config={mathjaxConfig}>
      <Box sx={{ maxWidth: 1100, margin: "0 auto" }}>
        <Box sx={{ p: 2 }}>
          <Stack spacing={2}>
            {/* Header */}
            <Stack spacing={1}>
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" justifyContent="space-between">
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                  <Typography variant="h5">{sheet.name || sheet.id}</Typography>
                  <Chip size="small" label={`v${sheet.version || "0.0.0"}`} />
                  <Chip size="small" label={sheet.visibility || "private"} />
                  {role ? <Chip size="small" label={`role: ${role}`} /> : null}
                </Stack>

                {isOwner ? (
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<EditIcon />}
                    onClick={() => navigate(`/sheet/${sheetId}/edit`)}
                  >
                    Edit
                  </Button>
                ) : null}
              </Stack>

              {sheet.description && viewType !== "simple" ? (
                <Typography variant="body2" sx={{ opacity: 0.85 }}>
                  {sheet.description}
                </Typography>
              ) : null}
            </Stack>

            <Divider />

            {/* View controls */}
            <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
              <ToggleButtonGroup
                size="small"
                value={viewType}
                exclusive
                onChange={(e, v) => v && setViewType(v)}
              >
                <ToggleButton value="simple">Simple</ToggleButton>
                <ToggleButton value="description">Description</ToggleButton>
                <ToggleButton value="basic">Basic</ToggleButton>
                <ToggleButton value="full">Full</ToggleButton>
              </ToggleButtonGroup>

              <FormControl size="small" sx={{ minWidth: 140 }}>
                <InputLabel>Columns</InputLabel>
                <Select label="Columns" value={columns} onChange={(e) => setColumns(Number(e.target.value))}>
                  <MenuItem value={1}>1</MenuItem>
                  <MenuItem value={2}>2</MenuItem>
                  <MenuItem value={3}>3</MenuItem>
                  <MenuItem value={4}>4</MenuItem>
                </Select>
              </FormControl>
            </Stack>

            {/* Global variables */}
            {visibleGlobalVars.length > 0 ? (
              <>
                <Divider />
                <Stack spacing={0.5}>
                  <Typography variant="subtitle1">Global variables</Typography>

                  {viewType === "basic" ? (
                    <Box sx={{ display: "flex", flexWrap: "wrap" }}>
                      {visibleGlobalVars.map((v, idx) => (
                        <VarChip key={v.id || idx} v={v} />
                      ))}
                    </Box>
                  ) : viewType === "full" ? (
                    <Stack spacing={0.1}>
                      {visibleGlobalVars.map((v, idx) => (
                        <VarInline key={v.id || idx} v={v} />
                      ))}
                    </Stack>
                  ) : null}
                </Stack>
              </>
            ) : null}

            {/* Sections */}
            {normalisedSections.length > 0 ? (
              <>
                <Divider />
                <Typography variant="subtitle1">Sections</Typography>

                <Box sx={{ columnCount: columns, columnGap: 2 }}>
                  {normalisedSections.map((s) => {
                    const sectionItems = s._items || [];
                    const sectionVars = s._vars || [];

                    const showSectionDesc = showDescriptions && !!(s.description || "").trim();
                    const showSectionVars = showVars && sectionVars.length > 0;

                    const hasAny = sectionItems.length > 0 || showSectionDesc || showSectionVars;
                    if (!hasAny) return null;

                    return (
                      <Box
                        key={s.id || s._idx}
                        sx={{
                          p: 1.25,
                          mb: 2,
                          display: "inline-block",
                          width: "100%",
                          breakInside: "avoid",
                        }}
                      >
                        <Stack spacing={1}>
                          <Stack spacing={0.25}>
                            <Typography sx={{ fontWeight: 800 }}>{s.name || "(unnamed section)"}</Typography>
                            {showSectionDesc ? (
                              <Typography variant="body2" sx={{ opacity: 0.85 }}>
                                {s.description}
                              </Typography>
                            ) : null}
                          </Stack>

                          {/* Section variables */}
                          {showSectionVars ? (
                            <>
                              {viewType === "basic" ? (
                                <Box sx={{ display: "flex", flexWrap: "wrap" }}>
                                  {sectionVars.map((v, idx) => (
                                    <VarChip key={idx} v={v} />
                                  ))}
                                </Box>
                              ) : viewType === "full" ? (
                                <Stack spacing={0.05}>
                                  {sectionVars.map((v, idx) => (
                                    <VarInline key={idx} v={v} />
                                  ))}
                                </Stack>
                              ) : null}

                              {sectionItems.length > 0 ? <Divider /> : null}
                            </>
                          ) : null}

                          {/* Items */}
                          {sectionItems.length > 0 ? (
                            <Stack spacing={1}>
                              {sectionItems.map((it, idx) => {
                                const type = (it.type || "text").toLowerCase();
                                const name = (it.name || "").trim();
                                const desc = (it.description || "").trim();

                                // Simple must show functions too (code always), and equations always.
                                // For text items, simple requires name to avoid junk rows.
                                if (viewType === "simple" && type !== "equation" && type !== "function" && !name) {
                                  return null;
                                }

                                return (
                                  <Box key={it.id || idx} sx={{ py: 0.25 }}>
                                    {/* item title (keep for all types if present) */}
                                    {name ? (
                                      <Typography sx={{ fontWeight: 700 }}>
                                        {name}
                                      </Typography>
                                    ) : null}

                                    {/* descriptions only in description/full (NOT simple/basic) */}
                                    {showDescriptions && desc ? (
                                      <Typography variant="body2" sx={{ opacity: 0.85 }}>
                                        {desc}
                                      </Typography>
                                    ) : null}

                                    {/* equation/function body ALWAYS visible */}
                                    {renderItemBody(it)}

                                    {/* variables: basic/full only, small */}
                                    {viewType === "basic" && Array.isArray(it.variables) && it.variables.length ? (
                                      <Box sx={{ display: "flex", flexWrap: "wrap", pt: 0.25 }}>
                                        {it.variables.map((v, vi) => (
                                          <VarChip key={vi} v={v} />
                                        ))}
                                      </Box>
                                    ) : null}

                                    {viewType === "full" && Array.isArray(it.variables) && it.variables.length ? (
                                      <Stack spacing={0.05} sx={{ pt: 0.1 }}>
                                        {it.variables.map((v, vi) => (
                                          <VarInline key={vi} v={v} />
                                        ))}
                                      </Stack>
                                    ) : null}
                                  </Box>
                                );
                              })}
                            </Stack>
                          ) : null}
                        </Stack>
                      </Box>
                    );
                  })}
                </Box>
              </>
            ) : null}
          </Stack>
        </Box>
      </Box>
    </MathJaxContext>
  );
}
